import React, { useRef, useEffect, useState } from "react";
import { connect } from "react-redux";
import ResultsGrid from "components/explore/resultsGrid";
import ResultsCharts from "components/explore/resultsCharts";
import { Resizable } from 're-resizable';
import { CloseableDialogTitle } from 'components/layout';
import { setExploreGridIsCollapsed, updateSelectedExploreView, fetchExplorePropertyData } from 'store/actions';
import { makeStyles } from "@material-ui/styles";
import { Typography, SvgIcon, Collapse, Grid, Button, Divider, Dialog, DialogContent, LinearProgress } from "@material-ui/core";
import { SpeedDial, SpeedDialAction, SpeedDialIcon } from '@material-ui/lab/';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import _ from "lodash";

const useStyles = makeStyles(theme => ({
  speedDial: {
    position: 'absolute', right: 60, bottom: 36,
    color: '#efefef'
  },
  controlsContainer: {
    height: 48,
    borderBottom: '1px solid #e0e0e0'
  },
  dropDown: {
    margin: 8
  },
  sourceHiddenOverlay: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(200, 200, 200, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3
  },
  sourceHiddenOverlayMobile: {
    position: 'absolute',
    width: '100%',
    backgroundColor: 'rgba(200, 200, 200, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '-webkit-fill-available',
    zIndex: 3
  },
  mobileSourcehidden: {
    backgroundColor: 'rgba(200, 200, 200, 0.9)',
    pointerEvents: 'none'
  },
  gridDialogContent: {
    height: '100%',
    padding: 0,
    margin: 0,
  }
}));

function ExploreResults({ openPushPropertiesModal, selectedView, propertyDataDisplay,
  updateSelectedExploreView, fetchExplorePropertyData, isMobile, rowCount, isMainIndicatorVisible }) {
  const classes = useStyles();
  const gridResizable = useRef(null);

  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [resizableHeight, setResizableHeight] = useState(0);
  const [isColumnChooserVisible, setIsColumnChooserVisible] = useState(false);
  const [isGridFullScreen, setIsGridFullScreen] = useState(false);

  useEffect(() => {
    window.addEventListener("resize", updateResizableSizeDebounced);
    return () => {
      window.removeEventListener("resize", updateResizableSizeDebounced);
    }
  }, []);

  useEffect(() => {
    if (!resizableHeight && gridResizable.current) {
      updateResizableSize();
    }
  }, [propertyDataDisplay, gridResizable]);

  useEffect(() => {
    if (!propertyDataDisplay) return;
    if (!propertyDataDisplay.visible || propertyDataDisplay.type !== 'Grid') {
      setIsColumnChooserVisible(false);
    }
  }, [propertyDataDisplay]);

  const sourceId = propertyDataDisplay?.selectedSourceId;
  useEffect(() => {
    setIsColumnChooserVisible(false);
  }, [sourceId]);

  const updateResizableSizeDebounced = _.debounce(updateResizableSize, 500);
  function updateResizableSize() {
    if (!gridResizable.current) return;
    setResizableHeight(gridResizable.current.resizable.clientHeight);
    setContainerWidth(window.innerWidth);
  }

  if (!propertyDataDisplay) return null;
  function handleChangeResultsType(value) {
    updateSelectedExploreView({ propertyDataDisplay: { ...propertyDataDisplay, type: value } });
  }

  const renderSpeedDial = () => (
    <>
      {propertyDataDisplay?.visible &&
        <SpeedDial
          ariaLabel="Aggregate View Type"
          className={classes.speedDial}
          color="secondary"
          open={speedDialOpen}
          onOpen={(e, reason) => {
            if (reason !== 'toggle') return;
            setSpeedDialOpen(true)
          }}
          onClose={(e, reason) => {
            if (reason !== 'toggle' && reason !== 'blur') return;
            setSpeedDialOpen(false)
          }}
          icon={<SpeedDialIcon />}
        >
          <SpeedDialAction
            icon={propertyDataDisplay?.visible
              ? <ExpandMoreIcon />
              : <ExpandLessIcon />
            }
            tooltipTitle={propertyDataDisplay?.visible ? 'Collapse grid' : 'Expand grid'}
            onClick={() => {
              updateSelectedExploreView({ propertyDataDisplay: { ...propertyDataDisplay, visible: !propertyDataDisplay?.visible } });
              setIsGridFullScreen(false);
              setSpeedDialOpen(false);
            }}
          />
          <SpeedDialAction
            icon={propertyDataDisplay.type === "Grid"
              ? <SvgIcon>
                <path d="M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z" />
              </SvgIcon>
              : <SvgIcon>
                <path d="M4,3H20A2,2 0 0,1 22,5V20A2,2 0 0,1 20,22H4A2,2 0 0,1 2,20V5A2,2 0 0,1 4,3M4,7V10H8V7H4M10,7V10H14V7H10M20,10V7H16V10H20M4,12V15H8V12H4M4,20H8V17H4V20M10,12V15H14V12H10M10,20H14V17H10V20M20,20V17H16V20H20M20,12H16V15H20V12Z" />
              </SvgIcon>
            }
            tooltipTitle={propertyDataDisplay.type === 'Grid' ? 'Charts view' : 'Grid view'}
            onClick={() => handleChangeResultsType(propertyDataDisplay?.type === 'Grid' ? 'Charts' : 'Grid')}
          />
          {propertyDataDisplay.type === 'Grid' &&
            <SpeedDialAction
              icon={<i className="dx-icon-columnchooser" style={{ fontSize: '18px' }}></i>}
              tooltipTitle={'Grid column chooser'}
              onClick={() => setIsColumnChooserVisible(true)}
            />
          }
        </SpeedDial>
      }
    </>
  )

  const resizeableGrid = () => {
    return <>
      <Collapse in={propertyDataDisplay?.visible} style={{ width: '100%' }} timeout={0}>
        <Resizable
          ref={gridResizable}
          minHeight='20vh' // Use vh/vw or px only, not %. Limitation of re-resizable- % of parent does not calculate correctly in a vertical flex layout
          maxHeight='70vh'
          defaultSize={{ height: ('30vh') }}
          enable={{ top: true, right: false, bottom: false, left: false, topRight: false, bottomRight: false, bottomLeft: false, topLeft: false }}
          onResize={updateResizableSizeDebounced}
        >
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
            {propertyDataDisplay.type === "Charts"
              ? <ResultsCharts resizableHeight={resizableHeight} containerWidth={containerWidth} />
              : <ResultsGrid openPushPropertiesModal={openPushPropertiesModal} isColumnChooserVisible={isColumnChooserVisible} selectedSourceVisible={selectedSourceVisible} setIsColumnChooserVisible={setIsColumnChooserVisible} />
            }
            {(!hasSelectedSource || !selectedSourceVisible) &&
              <div className={classes.sourceHiddenOverlay}>
                <Typography variant='subtitle2' >{hasSelectedSource ? 'Visibility for the selected Property Data object is set to hidden' : 'No Property Data object selected as source'}</Typography>
              </div>
            }
          </div>
        </Resizable>
      </Collapse>
      {renderSpeedDial()}
    </>
  }

  const hasSelectedSource = selectedView?.propertyDataDisplay.selectedSourceId != null;
  const selectedSourceVisible = hasSelectedSource &&
    selectedView.objects.find(obj => obj.id === selectedView.propertyDataDisplay.selectedSourceId)?.visible;

  return isMobile ? (
    <>
    <div style={{height: '5px', width: '100%'}}> 
      {isMainIndicatorVisible && <LinearProgress style={{ margin: '2px -8px -3px'}}/>}
    </div>
      {propertyDataDisplay?.visible && (
        <Grid container style={{ justifyContent: 'center', alignItems: 'center' }} spacing={0}>
          <Typography variant='subtitle2' style={{ marginRight: 8 }}>{rowCount + ' records'}</Typography>
          <Button variant='outlined' color='primary' size='small' onClick={() => setIsGridFullScreen(true)} style={{ margin: 4 }}> Open Grid </Button>
          {(!hasSelectedSource || !selectedSourceVisible) && (
            <div className={classes.sourceHiddenOverlayMobile}>
              <Typography style={{padding: 2}} variant='subtitle2' >{hasSelectedSource ? 'Visibility for the selected Property Data object is set to hidden' : 'No Property Data object selected as source'}</Typography>
            </div>
          )}
        </Grid>
      )}
    
      <Dialog keepMounted open={isGridFullScreen} onClose={() => setIsGridFullScreen(false)} fullScreen={true}>
        <CloseableDialogTitle id="map-grid-dialog-title" onClose={() => setIsGridFullScreen(false)}>
          <Typography variant='h6' style={{ marginRight: 8 }}>Active Map Data</Typography>
        </CloseableDialogTitle>
        <Divider />
        <DialogContent style={{padding: '0px'}}>
          <Grid container style={{ flexGrow: 1, height: '100%' }} spacing={0}>
            {propertyDataDisplay.type === "Charts"
              ? <ResultsCharts />
              : <ResultsGrid openPushPropertiesModal={openPushPropertiesModal} isColumnChooserVisible={isColumnChooserVisible} selectedSourceVisible={selectedSourceVisible} setIsColumnChooserVisible={setIsColumnChooserVisible} />
            }
          </Grid>
          {renderSpeedDial()}
        </DialogContent>
      </Dialog>
    </>
  ) : resizeableGrid()
}

const mapState = (state) => {
  const { exploreData, exploreMap, exploreViews } = state;
  const { selectedView } = exploreViews;
  const selectedId = exploreViews.selectedView?.propertyDataDisplay?.selectedSourceId;
  const pdo = selectedId != null ? exploreData.propertyDataObjects[selectedId] : null;
  const rowCount = pdo?.propertyData?.length;
  return {
    propertyDataDisplay: selectedView?.propertyDataDisplay,
    selectedView,
    gridIsCollapsed: exploreMap.gridIsCollapsed,
    rowCount: rowCount ?? 0,
    isMainIndicatorVisible: pdo?.isMainIndicatorVisible,
  };
}

const mapDispatch = {
  setExploreGridIsCollapsed,
  updateSelectedExploreView,
  fetchExplorePropertyData
}

export default connect(
  mapState,
  mapDispatch
)(ExploreResults);